<template>
  <div
    :id="`${source}_otp_fields`"
    class="flex flex-col items-center justify-center gap-2"
  >
    <div class="flex flex-row gap-1 items-center">
      <a-input-number
        v-for="(digit, index) in digits"
        :key="index"
        :style="inputStyle"
        placeholder="0"
        :value="digit"
        @input="updateDigit(index, $event)"
        :min="0"
        :max="9"
        :controls="false"
        @keydown="handleKeydown($event, index)"
        @paste="handlePaste($event, index)"
        ref="inputRefs"
        :class="{ error_border: error }"
        maxlength="1"
      />
    </div>
    <span
      v-if="error && errorMessage"
      class="text-sm text-center error_message_color"
      >{{ errorMessage }}</span
    >
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";

const props = defineProps({
  source: { type: String, required: true },
  length: { type: Number, default: 6 },
  font: { type: Number, default: 14 },
  width: { type: Number, default: 36 },
  height: { type: Number, default: 36 },
  padding: { type: String, default: "p-2" },
  value: { type: String, default: "" },
  error: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
});

const emit = defineEmits(["update:value", "update:enter"]);

const inputRefs = ref([]);
const digits = ref(Array(props.length).fill(""));

watch(
  () => props.value,
  (newValue) => {
    if (newValue) {
      const valueArray = newValue.toString().split("");
      digits.value = Array(props.length)
        .fill("")
        .map((_, i) => valueArray[i] || "");
    } else {
      digits.value = Array(props.length).fill("");
    }
  },
  { immediate: true },
);

watch(
  digits,
  (newDigits) => {
    const value = newDigits.join("");
    emit("update:value", value);
  },
  { deep: true },
);

const inputStyle = computed(() => ({
  width: `${props.width}px !important`,
  height: `${props.height}px `,
  fontSize: `${props.font}px !important`,
  padding: `${props.padding}px !important`,
}));

const updateDigit = async (index, value) => {
  try {
    if (value !== null && value !== undefined) {
      digits.value[index] = value.toString();

      if (value !== "" && index < props.length - 1) {
        await nextTick();
        inputRefs.value[index + 1]?.$el.querySelector("input")?.focus();
      }
    }
  } catch (error) {
    console.error("An error occurred while updating digit: ", error);
  }
};

const handleKeydown = async (event, index) => {
  try {
    if (event.key === "Enter") {
      event.preventDefault();
      emit("update:enter");
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      if (index < props.length - 1) {
        await nextTick();
        inputRefs.value[index + 1]?.$el.querySelector("input")?.focus();
      }
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (index > 0) {
        await nextTick();
        inputRefs.value[index - 1]?.$el.querySelector("input")?.focus();
      }
    }

    if (event.key === "Backspace" && !digits.value[index] && index > 0) {
      await nextTick();
      inputRefs.value[index - 1]?.$el.querySelector("input")?.focus();
    }
  } catch (error) {
    console.error("An error occurred while handling key down: ", error);
  }
};

const handlePaste = async (event, index) => {
  try {
    event.preventDefault();

    const pasteData = event.clipboardData.getData("text");
    const pasteDigits = pasteData
      .replace(/\D/g, "")
      .split("")
      .slice(0, props.length);

    if (pasteDigits.length > 0) {
      const newDigits = [...digits.value];

      pasteDigits.forEach((digit, i) => {
        if (index + i < props.length) {
          newDigits[index + i] = digit;
        }
      });

      digits.value = newDigits;

      const nextFocusIndex = Math.min(
        index + pasteDigits.length,
        props.length - 1,
      );
      await nextTick();
      inputRefs.value[nextFocusIndex]?.$el.querySelector("input")?.focus();
    }
  } catch (error) {
    console.error("An error occurred while handling paste: ", error);
  }
};

onMounted(async () => {
  await nextTick();
  inputRefs.value[0]?.$el.querySelector("input")?.focus();
});
</script>

<style scoped>
:deep(.error_border) {
  border-color: var(--error-color) !important;
}

:deep(.error_border:hover) {
  border-color: var(--error-color) !important;
}

:deep(.error_border.ant-input-number-focused) {
  border-color: var(--error-color) !important;
}

:deep(.ant-input-number .ant-input-number-input) {
  padding: 0 !important;
  text-align: center !important;
}

.error_message_color {
  color: var(--error-color);
}
</style>
